import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSearch } from '../context/searchcontext'
import { performLocationSearch } from '../utils/searchService'
import { buildSlugPath } from '../utils/slugutils'
import Toast from './Toast'

// ─── Same option lists as searchbar.js ───────────────────────────────────────
const GENDER_OPTIONS  = ['Any Gender', 'Boys', 'Girls', 'Co-ed']
const PRICE_OPTIONS   = ['Any Price', 'Under ₹5,000', '₹5,000–₹8,000', '₹8,000–₹12,000', 'Above ₹12,000']
const SHARING_OPTIONS = ['Any Type', 'Single', 'Double', 'Triple']

// ─────────────────────────────────────────────────────────────────────────────
// HeroSearchBar
//
// Renders the SAME mobile-card markup as SearchBar so all the hero-scoped
// _home.scss rules apply identically.
//
// The only difference: clicking Gender / Price / Sharing opens a compact
// dropdown anchored below that chip (like the desktop sb-dropdown-options)
// instead of the full-screen centred overlay modal used in the navbar version.
//
// State is shared through SearchContext — searching here updates the same
// values the navbar SearchBar reads, and vice-versa.
// ─────────────────────────────────────────────────────────────────────────────
const HeroSearchBar = () => {
  const navigate  = useNavigate()
  const {
    location,  setLocation,
    gender,    setGender,
    price,     setPrice,
    sharing,   setSharing,
    setSearchResults,
    setNearbyPincodes,
    setSearchCoordinates,
    setIsSearching,
  } = useSearch()

  // Local UI state
  const [searchQuery,        setSearchQuery]        = useState('')
  const [suggestions,        setSuggestions]        = useState([])
  const [showDropdown,       setShowDropdown]       = useState(false)
  const [activeFilter,       setActiveFilter]       = useState(null)  // 'gender' | 'price' | 'sharing' | null
  const [isLoading,          setIsLoading]          = useState(false)
  const [toast,              setToast]              = useState(null)

  const wrapperRef       = useRef(null)
  const searchTimeoutRef = useRef(null)

  const showToast = (message, type = 'error') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 3000)
  }

  // ── New Autocomplete using custom API ─────────────────────────────────────────
  const fetchSuggestions = async (query) => {
    if (!query || query.length < 2) { 
      setSuggestions([]); 
      setShowDropdown(false); 
      return 
    }

    try {
      const url = `https://ciplpgapi.azurewebsites.net/pgmaster/listing-location-search/${encodeURIComponent(query)}`
      console.log('🔍 Fetching suggestions from:', url)
      
      const response = await fetch(url, {
        headers: { 
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const data = await response.json()
      console.log('📦 Suggestions received:', data)
      
      // Transform API response to match the expected format
      // Expected format from your API: [{"SearchValue":"","SearchType":""}]
      const transformedSuggestions = (data || []).map(item => ({
        display_name: item.SearchValue,
        place_id: item.SearchValue, // Use SearchValue as unique key
        type: item.SearchType
      }))
      
      setSuggestions(transformedSuggestions)
      setShowDropdown(transformedSuggestions.length > 0)
    } catch (error) {
      console.error('Error fetching suggestions:', error)
      setSuggestions([])
      setShowDropdown(false)
    }
  }

  const handleInputChange = (e) => {
    const value = e.target.value
    setSearchQuery(value)
    setLocation(value)
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
    searchTimeoutRef.current = setTimeout(() => fetchSuggestions(value), 300)
  }

  // ── Search ────────────────────────────────────────────────────────────────
  const handleSearch = async (queryToSearch) => {
    const searchTerm = queryToSearch || searchQuery || location
    if (!searchTerm) { showToast('Please enter a location to search', 'warning'); return }
  
    // ✅ Save exact search term to localStorage for page refresh
    localStorage.setItem('lastSearchTerm', searchTerm)
    localStorage.setItem('lastSearchGender', gender || '')
    localStorage.setItem('lastSearchPrice', price || '')
    localStorage.setItem('lastSearchSharing', sharing || '')
  
    setIsLoading(true)
    setIsSearching(true)
    setShowDropdown(false)
    setActiveFilter(null)
  
    const result = await performLocationSearch(searchTerm, gender, price, sharing)

    if (result.success) {
      const isCitySearch = searchTerm.toLowerCase().includes('delhi')
      if (result.results.length === 0 && !isCitySearch)
        showToast(`No PGs found in ${searchTerm}. Try a different location.`, 'info')
      setSearchResults(result.results)
      setNearbyPincodes(result.nearbyPincodes)
      setSearchCoordinates(result.coordinates)
      setLocation(searchTerm)
      setSearchQuery(searchTerm)
      navigate(buildSlugPath(
        searchTerm,
        gender  !== 'Any Gender' ? gender  : '',
        price   !== 'Any Price'  ? price   : '',
        sharing !== 'Any Type'   ? sharing : '',
      ))
    } else {
      showToast(result.error || 'Location not found. Please try a different area.', 'error')
    }

    setIsLoading(false)
    setIsSearching(false)
  }

  // ── Geolocation ───────────────────────────────────────────────────────────
  const handleGeolocation = () => {
    if (!navigator.geolocation) { showToast('Geolocation not supported by your browser', 'error'); return }
    setIsLoading(true)
    navigator.geolocation.getCurrentPosition(
      async ({ coords: { latitude, longitude } }) => {
        const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`
        try {
          const res      = await fetch(url, { headers: { 'User-Agent': 'PGApp/1.0' } })
          const data     = await res.json()
          const areaName = data.display_name?.split(',')[0] || data.address?.suburb || data.address?.neighbourhood
          setSearchQuery(areaName)
          await handleSearch(areaName)
        } catch {
          showToast('Could not get your location. Please search manually.', 'error')
        }
        setIsLoading(false)
      },
      (err) => {
        showToast(err.code === 1 ? 'Please allow location access to use this feature.' : 'Unable to get your location.', 'warning')
        setIsLoading(false)
      },
    )
  }

  // ── Filter chip inline-dropdown handlers ──────────────────────────────────
  // Toggle: clicking the same chip closes it. Selecting an option applies
  // immediately (no separate Apply step) — matching desktop sb-dropdown UX.
  const toggleFilter = (type) => {
    setActiveFilter(prev => (prev === type ? null : type))
  }

  const selectOption = (type, opt) => {
    if (type === 'gender')  setGender(opt  === 'Any Gender' ? '' : opt)
    if (type === 'price')   setPrice(opt   === 'Any Price'  ? '' : opt)
    if (type === 'sharing') setSharing(opt === 'Any Type'   ? '' : opt)
    setActiveFilter(null)
  }

  const clearChip = (type, e) => {
    e.stopPropagation()
    if (type === 'gender')  setGender('')
    if (type === 'price')   setPrice('')
    if (type === 'sharing') setSharing('')
  }

  const getDisplay = (type) => {
    if (type === 'gender') return gender  || 'Any gender'
    if (type === 'price')  return price   || 'Any price'
    return sharing || 'Any type'
  }

  // ── Close on outside click ────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setActiveFilter(null)
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // ── Render ────────────────────────────────────────────────────────────────
  // Class names are identical to SearchBar's mobile card so every
  // .apg-hero__mobile-search rule in _home.scss applies without any change.
  return (
    <div className="sb-wrapper" ref={wrapperRef}>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* sb-bar is intentionally omitted — hero only needs the mobile card */}
      <div className="sb-mobile-card">

        {/* ── Location ──────────────────────────────────────────────── */}
        <div className="sb-mobile-location">
          <div className="sb-mobile-location-label">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
              <circle cx="12" cy="10" r="3"/>
            </svg>
            <span>WHERE TO STAY?</span>
          </div>

          <div className="sb-mobile-location-input-wrapper">
            <div className="sb-mobile-location-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
              </svg>
            </div>
            <input
              type="text"
              className="sb-mobile-location-input"
              placeholder="Search area, locality, or landmark"
              value={searchQuery || location}
              onChange={handleInputChange}
              onKeyDown={e => { if (e.key === 'Enter') handleSearch(); if (e.key === 'Escape') setShowDropdown(false) }}
              onFocus={() => { if (suggestions.length > 0) setShowDropdown(true) }}
            />
          </div>

          {/* Autocomplete suggestions — using new API response format */}
          {showDropdown && suggestions.length > 0 && (
            <div className="sb-mobile-suggestions">
              {suggestions.map((item, idx) => (
                <button
                  key={item.place_id || idx}
                  className="sb-mobile-suggestion"
                  onClick={() => {
                    const label = item.display_name
                    setSearchQuery(label)
                    setLocation(label)
                    setShowDropdown(false)
                    handleSearch(label)
                  }}
                >
                  <div className="sb-mobile-suggestion-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
                      <circle cx="12" cy="10" r="3"/>
                    </svg>
                  </div>
                  <div className="sb-mobile-suggestion-text">
                    <span className="sb-mobile-suggestion-title">{item.display_name}</span>
                    {item.type && (
                      <span className="sb-mobile-suggestion-sub">{item.type}</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}

          <div className="sb-mobile-current-location" onClick={handleGeolocation}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#155DFC" strokeWidth="2">
              <circle cx="12" cy="12" r="3"/>
              <path d="M12 2V5M12 19V22M2 12H5M19 12H22"/>
            </svg>
            <span>Use my current location</span>
          </div>
        </div>

        {/* ── Filter chips ───────────────────────────────────────────── */}
        {/*
         *  Each chip is wrapped in a `position: relative` div so its dropdown
         *  is anchored exactly below it — no overlay, no screen-centring.
         *  The dropdown reuses `sb-dropdown` + `sb-dropdown-options` + `sb-option`
         *  classes from _searchbar.scss so it is pixel-for-pixel identical to
         *  the desktop filter panels.
         */}
        <div className="sb-mobile-filters">
          <div className="sb-mobile-filters-scroll">

            {/* Gender */}
            <div className="hsb-chip-wrap">
              <div
                className={`sb-mobile-filter-chip ${gender ? 'active' : ''}`}
                onClick={() => toggleFilter('gender')}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="8" r="4" />
                  <path d="M5 20c0-3.5 3.5-6 7-6s7 2.5 7 6" />
                </svg>
                <span>{getDisplay('gender')}</span>
                {gender && (
                  <span className="clear-icon" onClick={e => clearChip('gender', e)}>✕</span>
                )}
              </div>

              {activeFilter === 'gender' && (
                <div
                  className="sb-dropdown sb-dropdown-options hsb-chip-dropdown"
                  onClick={e => e.stopPropagation()}
                >
                  {GENDER_OPTIONS.map(opt => (
                    <button
                      key={opt}
                      className={`sb-option ${(gender || 'Any Gender') === opt ? 'selected' : ''}`}
                      onClick={() => selectOption('gender', opt)}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Price */}
            <div className="hsb-chip-wrap">
              <div
                className={`sb-mobile-filter-chip ${price ? 'active' : ''}`}
                onClick={() => toggleFilter('price')}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2v20M17 7H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                </svg>
                <span>{getDisplay('price')}</span>
                {price && (
                  <span className="clear-icon" onClick={e => clearChip('price', e)}>✕</span>
                )}
              </div>

              {activeFilter === 'price' && (
                <div
                  className="sb-dropdown sb-dropdown-options hsb-chip-dropdown"
                  onClick={e => e.stopPropagation()}
                >
                  {PRICE_OPTIONS.map(opt => (
                    <button
                      key={opt}
                      className={`sb-option ${(price || 'Any Price') === opt ? 'selected' : ''}`}
                      onClick={() => selectOption('price', opt)}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Sharing */}
            <div className="hsb-chip-wrap hsb-chip-wrap--last">
              <div
                className={`sb-mobile-filter-chip ${sharing ? 'active' : ''}`}
                onClick={() => toggleFilter('sharing')}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
                  <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
                </svg>
                <span>{getDisplay('sharing')}</span>
                {sharing && (
                  <span className="clear-icon" onClick={e => clearChip('sharing', e)}>✕</span>
                )}
              </div>

              {activeFilter === 'sharing' && (
                <div
                  className="sb-dropdown sb-dropdown-options hsb-chip-dropdown sb-dropdown-right"
                  onClick={e => e.stopPropagation()}
                >
                  {SHARING_OPTIONS.map(opt => (
                    <button
                      key={opt}
                      className={`sb-option ${(sharing || 'Any Type') === opt ? 'selected' : ''}`}
                      onClick={() => selectOption('sharing', opt)}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              )}
            </div>

          </div>
        </div>

        {/* ── Search button ──────────────────────────────────────────── */}
        {/* <button className="sb-mobile-search-btn" onClick={() => handleSearch()} disabled={isLoading}>
          {isLoading ? 'Searching...' : (
            <>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5">
                <circle cx="11" cy="11" r="8"/>
                <path d="M21 21l-4.35-4.35"/>
              </svg>
              Search PGs
            </>
          )}
        </button> */}

      </div>
    </div>
  )
}

export default HeroSearchBar